// frontend/src/components/TasksView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { URL } from "../../utils/constants";
import "./TasksView.css";

export default function TasksView({ user, tokenProp }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canUsePanel, setCanUsePanel] = useState(false);

  const [searchType, setSearchType] = useState("text"); // text | id | address | inventory
  const [query, setQuery] = useState("");

  const canView = canUsePanel;
  const canManage = canUsePanel;

  useEffect(() => {
    let mounted = true;

    const norm = (s) => String(s ?? "").toLowerCase().trim();

    const applyFromObj = (u = {}) => {
      let groups = u.groups || u.group_names || [];

      if (Array.isArray(groups) && groups.length && typeof groups[0] === "object") {
        groups = groups.map((g) => g?.name);
      }

      const normalizedGroups = (groups || []).map(norm);
      const hasOCK = normalizedGroups.includes("ock");

      if (mounted) {
        setCanUsePanel(hasOCK);
      }

      return hasOCK;
    };

    try {
      const raw = localStorage.getItem("user");
      const parsed = JSON.parse(raw || "{}");
      if (parsed && (parsed.id || parsed.pk || (parsed.user && parsed.user.id))) {
        applyFromObj(parsed);
      }
    } catch (e) {
      console.warn("Не удалось прочитать user из localStorage:", e);
    }

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      if (mounted) setLoading(false);
      return () => {
        mounted = false;
      };
    }

    const endpoints = [
      `${URL}/api/me/`,
      `${URL}/api/users/me/`,
      `${URL}/users/me/`,
      `${URL}/api/auth/users/me/`,
    ];

    (async () => {
      for (const ep of endpoints) {
        try {
          const r = await fetch(ep, {
            headers: { Authorization: `Token ${token}` },
          });

          if (!r.ok) continue;

          const data = await r.json();
          applyFromObj(data);
          break;
        } catch (e) {
          console.warn("Ошибка получения профиля:", ep, e);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [tokenProp, user]);

  useEffect(() => {
    let cancelled = false;

    const loadPoints = async () => {
      const token = tokenProp || localStorage.getItem("auth_token");
      if (!token || !canView) {
        if (!cancelled) setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const res = await fetch(`${URL}/api/points-ock/`, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        const data = await res.json().catch(() => []);

        if (!res.ok) {
          throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
        }

        const items = Array.isArray(data) ? data : data.items || data.results || [];
        if (!cancelled) setPoints(items);
      } catch (e) {
        console.error("Failed to load OCK points:", e);
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (canView) {
      loadPoints();
    } else {
      setLoading(false);
      setPoints([]);
    }

    return () => {
      cancelled = true;
    };
  }, [canView, tokenProp]);

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("ru-RU");
  };

  const formatDateOnly = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("ru-RU");
  };

  const getFullName = (person) => {
    if (!person) return "";
    return (
      person.full_name ||
      `${person.last_name || ""} ${person.first_name || ""}`.trim() ||
      person.username ||
      ""
    );
  };

  const getCreatorName = (p) => {
    return (
      p.creator_full_name ||
      `${p.creator_last_name || ""} ${p.creator_first_name || ""}`.trim() ||
      p.creator_username ||
      "—"
    );
  };

  const renderExecutors = (executors) => {
    if (!Array.isArray(executors) || executors.length === 0) return "—";
    return executors
      .map((e) => getFullName(e) || `ID ${e?.id ?? "?"}`)
      .join(", ");
  };

  const getObjectsText = (p) => {
    const list = Array.isArray(p.objects_ock_detail) ? p.objects_ock_detail : [];
    if (!list.length) {
      if (p.object_ref_detail?.name) return p.object_ref_detail.name;
      return "—";
    }
    return list.map((o) => o?.name).filter(Boolean).join(", ") || "—";
  };

  const getStagesText = (p) => {
    const list = Array.isArray(p.stages_detail) ? p.stages_detail : [];
    if (!list.length) {
      if (p.main_stage_detail?.name) return p.main_stage_detail.name;
      return "—";
    }
    return list.map((s) => s?.name).filter(Boolean).join(", ") || "—";
  };

  const getPointTitle = (p) => {
    return p.name || p.title || p.object_ref_detail?.name || p.address || `Точка ОСК #${p.id}`;
  };

  const statusToRu = (status) => {
    const s = String(status ?? "").trim().toLowerCase();

    const map = {
      new: "Новая",
      created: "Создана",
      in_work: "В работе",
      in_progress: "В работе",
      progress: "В работе",
      done: "Завершена",
      completed: "Завершена",
      closed: "Закрыта",
      canceled: "Отменена",
      cancelled: "Отменена",
      postponed: "Отложена",
      pause: "На паузе",
      paused: "На паузе",
      archive: "В архиве",
      archived: "В архиве",
    };

    return map[s] || status || "—";
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return points;

    return points.filter((p) => {
      const id = String(p.id ?? "");
      const pointTitle = String(getPointTitle(p) ?? "").toLowerCase();
      const address = String(p.address ?? "").toLowerCase();
      const inventory = String(p.inventory_number ?? "").toLowerCase();
      const status = String(statusToRu(p.status) ?? "").toLowerCase();
      const lat = String(p.lat ?? "").toLowerCase();
      const long = String(p.long ?? "").toLowerCase();
      const manager = String(p.manager_username ?? "").toLowerCase();
      const creator = String(getCreatorName(p) ?? "").toLowerCase();
      const mainStage = String(p.main_stage_detail?.name ?? "").toLowerCase();
      const objects = String(getObjectsText(p) ?? "").toLowerCase();
      const stages = String(getStagesText(p) ?? "").toLowerCase();
      const plannedFinish = String(p.planned_finish_date ?? "").toLowerCase();

      const executors = Array.isArray(p.executors_detail)
        ? p.executors_detail.map((e) => String(getFullName(e)).toLowerCase()).join(" ")
        : "";

      if (searchType === "id") return id.includes(q);
      if (searchType === "address") return address.includes(q);
      if (searchType === "inventory") return inventory.includes(q);

      return (
        id.includes(q) ||
        pointTitle.includes(q) ||
        address.includes(q) ||
        inventory.includes(q) ||
        status.includes(q) ||
        lat.includes(q) ||
        long.includes(q) ||
        manager.includes(q) ||
        creator.includes(q) ||
        mainStage.includes(q) ||
        executors.includes(q) ||
        objects.includes(q) ||
        stages.includes(q) ||
        plannedFinish.includes(q)
      );
    });
  }, [points, query, searchType]);

  const clearSearch = () => setQuery("");

  const deletePoint = async (pointId) => {
    if (!canManage) return;

    const ok = window.confirm("Удалить точку ОСК?");
    if (!ok) return;

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      alert("Нет токена авторизации");
      return;
    }

    try {
      const res = await fetch(`${URL}/api/points-ock/${pointId}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
      }

      setPoints((prev) => prev.filter((p) => String(p.id) !== String(pointId)));
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Ошибка удаления точки");
    }
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const exportToExcel = () => {
    try {
      const rowsHtml = filtered
        .map((p) => {
          const row = [
            getPointTitle(p),
            p.address ?? "",
            getCreatorName(p),
            renderExecutors(p.executors_detail),
            formatDateTime(p.created_at),
            statusToRu(p.status),
            formatDateOnly(p.planned_finish_date),
            p.inventory_number ?? "",
            getObjectsText(p),
            getStagesText(p),
          ];

          return `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`;
        })
        .join("");

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook>
                <x:ExcelWorksheets>
                  <x:ExcelWorksheet>
                    <x:Name>Точки ОСК</x:Name>
                    <x:WorksheetOptions>
                      <x:DisplayGridlines/>
                    </x:WorksheetOptions>
                  </x:ExcelWorksheet>
                </x:ExcelWorksheets>
              </x:ExcelWorkbook>
            </xml>
            <![endif]-->
            <style>
              table { border-collapse: collapse; width: 100%; }
              th, td {
                border: 1px solid #999;
                padding: 6px 8px;
                vertical-align: top;
                mso-number-format: "\\@";
              }
              th {
                background: #f2f2f2;
                font-weight: 700;
              }
            </style>
          </head>
          <body>
            <table>
              <thead>
                <tr>
                  <th>Название точки</th>
                  <th>Адрес точки</th>
                  <th>Создатель</th>
                  <th>Исполнитель</th>
                  <th>Дата создания</th>
                  <th>Статус</th>
                  <th>Дата сдачи</th>
                  <th>Инвентарный номер</th>
                  <th>Объекты</th>
                  <th>Этапы</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </body>
        </html>
      `;

      const blob = new Blob([html], {
        type: "application/vnd.ms-excel;charset=utf-8;",
      });

      const link = document.createElement("a");
      const fileUrl = window.URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().slice(0, 10);

      link.href = fileUrl;
      link.download = `ock_points_${dateStr}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);
    } catch (e) {
      console.error("Ошибка экспорта:", e);
      alert("Не удалось выгрузить таблицу");
    }
  };

  if (!canView) return <div>Доступно только пользователям группы OCK</div>;
  if (loading) return <div>Загрузка точек ОСК...</div>;

  return (
    <div
      className="tasks-view"
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{`
        .tasks-view__scroll {
          max-height: calc(100vh - 280px);
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 8px;
          scrollbar-width: auto;
          scrollbar-color: #8f8f8f #ececec;
        }

        .tasks-view__scroll::-webkit-scrollbar {
          width: 12px;
        }

        .tasks-view__scroll::-webkit-scrollbar-track {
          background: #ececec;
          border-radius: 10px;
        }

        .tasks-view__scroll::-webkit-scrollbar-thumb {
          background: #8f8f8f;
          border-radius: 10px;
          border: 2px solid #ececec;
        }

        .tasks-view__scroll::-webkit-scrollbar-thumb:hover {
          background: #6f6f6f;
        }
      `}</style>

      <h2 className="tasks-view__title">Точки ОСК</h2>

      <div
        className="dd-toolbar"
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          marginBottom: 12,
          flex: "0 0 auto",
        }}
      >
        <div
          className="dd-searchWrap dd-searchWrap--compact"
          style={{
            minWidth: 0,
            flex: "1 1 520px",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <select
            className="dd-searchSelect"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            aria-label="Тип поиска"
          >
            <option value="text">Текст</option>
            <option value="id">ID точки</option>
            <option value="address">Адрес</option>
            <option value="inventory">Инв. номер</option>
          </select>

          <input
            className="dd-search dd-search--compact"
            placeholder="Поиск точек ОСК…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              minWidth: 0,
              width: "100%",
              boxSizing: "border-box",
            }}
          />

          <button
            className="dd-clearSide"
            onClick={clearSearch}
            title="Очистить"
            type="button"
          >
            ✕
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            flex: "0 0 auto",
          }}
        >
          <button className="dd-btn dd-btn-primary" onClick={exportToExcel} type="button">
            Выгрузить в Excel
          </button>
        </div>
      </div>

      <div
        className="tasks-view__submeta"
        style={{
          marginBottom: 12,
          wordBreak: "break-word",
          flex: "0 0 auto",
        }}
      >
        <span>
          Найдено: <b>{filtered.length}</b>
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="tasks-view__empty">Ничего не найдено</div>
      ) : (
        <div
          className="dd-list tasks-view__list tasks-view__scroll"
          style={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {filtered.map((p) => {
            const id = p.id;
            const pointTitle = getPointTitle(p);
            const address = p.address ?? "—";
            const status = statusToRu(p.status);
            const inventory = p.inventory_number ?? "—";
            const lat = p.lat ?? "—";
            const long = p.long ?? "—";
            const manager = p.manager_username ?? "—";
            const creator = getCreatorName(p);
            const mainStage = p.main_stage_detail?.name ?? "—";
            const tasksCount = Array.isArray(p.tasks) ? p.tasks.length : 0;
            const created = formatDateTime(p.created_at);
            const updated = formatDateTime(p.updated_at);
            const plannedFinish = formatDateOnly(p.planned_finish_date) || "—";
            const executors = renderExecutors(p.executors_detail);
            const objects = getObjectsText(p);
            const stages = getStagesText(p);

            return (
              <div
                key={id}
                className="dd-item tasks-view__item"
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  overflow: "visible",
                  height: "auto",
                  minHeight: "unset",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  flex: "0 0 auto",
                }}
              >
                <div
                  className="dd-itemText tasks-view__text"
                  style={{
                    flex: "1 1 700px",
                    minWidth: 0,
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflow: "visible",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  <div
                    className="tasks-view__topline"
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      width: "100%",
                    }}
                  >
                    <span
                      className="tasks-view__titleText"
                      style={{
                        minWidth: 0,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {pointTitle}
                    </span>

                    <span
                      className="tasks-view__meta"
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        minWidth: 0,
                        maxWidth: "100%",
                      }}
                    >
                      <span className="tasks-view__pill">ID: {id}</span>
                      <span className="tasks-view__pill">Статус: {status}</span>
                      <span className="tasks-view__pill">Инв. №: {inventory}</span>
                      <span className="tasks-view__pill">Этап: {mainStage}</span>
                      <span className="tasks-view__pill">Задач: {tasksCount}</span>
                    </span>
                  </div>

                  <div className="tasks-view__desc" style={{ whiteSpace: "normal" }}>
                    <b>Адрес:</b> {address}
                  </div>

                  <div className="tasks-view__desc" style={{ whiteSpace: "normal" }}>
                    <b>Координаты:</b> {lat}, {long}
                  </div>

                  <div
                    className="tasks-view__submeta"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                      whiteSpace: "normal",
                    }}
                  >
                    <span><b>Создатель:</b> {creator}</span>
                    <span><b>Менеджер:</b> {manager}</span>
                  </div>

                  <div className="tasks-view__submeta" style={{ whiteSpace: "normal" }}>
                    <span><b>Исполнители:</b> {executors}</span>
                  </div>

                  <div className="tasks-view__submeta" style={{ whiteSpace: "normal" }}>
                    <span><b>Объекты:</b> {objects}</span>
                  </div>

                  <div className="tasks-view__submeta" style={{ whiteSpace: "normal" }}>
                    <span><b>Этапы:</b> {stages}</span>
                  </div>

                  <div
                    className="tasks-view__submeta"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 12,
                      whiteSpace: "normal",
                    }}
                  >
                    <span><b>Дата создания:</b> {created || "—"}</span>
                    <span><b>Дата обновления:</b> {updated || "—"}</span>
                    <span><b>Дата сдачи:</b> {plannedFinish}</span>
                  </div>
                </div>

                {canManage ? (
                  <div
                    className="tasks-view__actions"
                    style={{
                      flex: "0 0 auto",
                      alignSelf: "flex-start",
                    }}
                  >
                    <button
                      className="dd-btn tasks-view__miniBtn tasks-view__dangerBtn"
                      onClick={() => deletePoint(id)}
                      type="button"
                    >
                      Удалить
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}